import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { BrandDto } from "@insite/client-framework/Types/ApiModels";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useLaunchDarklyFlags from "@MasterBluePrint/Common/Hooks/useLaunchDarklyFlags";
import { LaunchDarklyFlagKeys } from "@MasterBluePrint/Common/Utilities/LaunchDarklyFlagKeys";
import { isMobileDevice } from "@MasterBluePrint/Overrides/Common/Utilities/isMobileDevice";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    brand: BrandDto;
    /**
     * If true, the small brand logo will be displayed.
     * If false, the brand name will be displayed instead.
     * Default value: false */
    showLogo?: boolean;
    extendedStyles?: ProductBrandStyles;
    fromPage?: string;
}

type Props = OwnProps;

export interface ProductBrandStyles {
    brandContainer?: InjectableCss;
    logoImage?: LazyImageProps;
    nameLink?: LinkPresentationProps;
    nameText?: TypographyProps;
}

const nameText: TypographyProps = {
    css: css`
        width: 100%;
        ${wrapInContainerStyles}
    `,
    size: "13px",
    weight: 600,
};

export const productBrandStyles: ProductBrandStyles = {
    nameText,
    nameLink: {       
        typographyProps: nameText,
    }
};

const ProductBrand: FC<Props> = ({ brand, showLogo = false, extendedStyles, fromPage }) => {
    let featureFlags: string[] = [LaunchDarklyFlagKeys.TEUS_8655];
    const flagValues = useLaunchDarklyFlags({ featureFlags });
    const isFeatureOn = flagValues[LaunchDarklyFlagKeys.TEUS_8655];
    const isFromProductListPage = fromPage === "productlist";
    if (!brand) {
        return null;
    }

    const styles = mergeToNew(productBrandStyles, extendedStyles);

    let brandDisplay;

    if (showLogo && brand.logoSmallImagePath) {
        const altText = brand.logoImageAltText || brand.name;
        brandDisplay = <Link {...styles.nameLink} href={`${brand.detailPagePath  }/category/all`} data-test-selector="brandLink">
            <LazyImage {...styles.logoImage} src={brand.logoSmallImagePath} altText={altText} /></Link>;
    }
    else if (showLogo && !brand.logoSmallImagePath) {
        brandDisplay = <Link {...styles.nameLink} href={`${brand.detailPagePath  }/category/all`} data-test-selector="brandLink">
            {brand.name}
        </Link>;
    }
    else {
        brandDisplay = brand.name
    }

    return (
        <>
            {(isFeatureOn && isFromProductListPage) ? (
                <Link {...styles.nameLink} href={`${brand.detailPagePath}/category/all`} data-test-selector="brandLink" className="lnk-brandname">
                    {brand.name}
                </Link>
            ) : (
                <Typography {...styles.brandContainer} className="brand-name">{brandDisplay}</Typography>
            )}
        </>
    );
};

export default ProductBrand;
